import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  Users,
  Plus,
  Search,
  Package,
  TrendingUp,
  MapPin,
  Clock,
  CheckCircle,
  AlertCircle,
  Eye,
  Edit,
  FileText,
  BarChart3,
} from 'lucide-react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { api } from '@/services/api';

interface InformalSupplier {
  id: string;
  name: string;
  type: 'formal' | 'informal' | 'mixed';
  contactInfo?: {
    phone?: string;
    email?: string;
    location?: string;
  };
  verificationStatus: 'verified' | 'unverified' | 'pending';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface InformalTransaction {
  id: string;
  supplierId: string;
  supplierType: 'formal' | 'informal' | 'mixed';
  transactionTag: 'cash_purchase' | 'market_purchase' | 'farmer_purchase' | 'informal_supplier_purchase';
  purchaseDate: string;
  items: Array<{
    name: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    category: string;
  }>;
  totalAmount: number;
  paymentMethod: 'cash' | 'mobile' | 'trade';
  location?: string;
  receiptNumber?: string;
  metadata: {
    source: string;
    gpsLocation?: {
      latitude: number;
      longitude: number;
    };
    photos?: string[];
  };
}

const InformalEconomyPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'suppliers' | 'transactions' | 'batch' | 'stats'>('suppliers');
  const [searchTerm, setSearchTerm] = useState('');
  const [supplierFilter, setSupplierFilter] = useState<string>('all');
  const [, setShowAddSupplierModal] = useState(false);
  const [, setShowAddTransactionModal] = useState(false);

  // Fetch suppliers
  const { data: suppliers = [] } = useQuery<InformalSupplier[]>({
    queryKey: ['informal-economy', 'suppliers', supplierFilter],
    queryFn: async () => {
      const res = await api.get(`/api/informal-economy/suppliers${supplierFilter !== 'all' ? `?type=${supplierFilter}` : ''}`);
      return res.data.data;
    },
  });

  // Fetch transactions
  const { data: transactions = [] } = useQuery<InformalTransaction[]>({
    queryKey: ['informal-economy', 'transactions'],
    queryFn: async () => {
      const res = await api.get('/api/informal-economy/transactions');
      return res.data.data;
    },
  });

  // Fetch statistics
  const { data: stats } = useQuery({
    queryKey: ['informal-economy', 'stats'],
    queryFn: async () => {
      const res = await api.get('/api/informal-economy/stats');
      return res.data.data;
    },
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-MW', {
      style: 'currency',
      currency: 'MWK',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-MW', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getSupplierTypeColor = (type: string) => {
    switch (type) {
      case 'formal':
        return 'bg-green-100 text-green-800';
      case 'informal':
        return 'bg-yellow-100 text-yellow-800';
      case 'mixed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getVerificationStatusIcon = (status: string) => {
    switch (status) {
      case 'verified':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-red-600" />;
    }
  };

  const getTransactionTagColor = (tag: string) => {
    switch (tag) {
      case 'cash_purchase':
        return 'bg-gray-100 text-gray-800';
      case 'market_purchase':
        return 'bg-purple-100 text-purple-800';
      case 'farmer_purchase':
        return 'bg-green-100 text-green-800';
      case 'informal_supplier_purchase':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredSuppliers = suppliers.filter(supplier =>
    supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.contactInfo?.phone?.includes(searchTerm) ||
    supplier.contactInfo?.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredTransactions = transactions.filter(transaction =>
    transaction.items.some(item => 
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    ) ||
    transaction.receiptNumber?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Informal Economy</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage informal suppliers and transactions with proper classification
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {activeTab === 'suppliers' && (
            <Button onClick={() => setShowAddSupplierModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Supplier
            </Button>
          )}
          {activeTab === 'transactions' && (
            <Button onClick={() => setShowAddTransactionModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Transaction
            </Button>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Suppliers</p>
                <p className="text-2xl font-bold text-gray-900">{stats.supplierBreakdown.formal + stats.supplierBreakdown.informal + stats.supplierBreakdown.mixed}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Transactions</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalTransactions}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <Package className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Amount</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalAmount)}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Estimated Tax</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.estimatedTaxTotal)}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <BarChart3 className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-lg">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { id: 'suppliers', label: 'Suppliers', icon: Users },
              { id: 'transactions', label: 'Transactions', icon: Package },
              { id: 'batch', label: 'Batch Transactions', icon: FileText },
              { id: 'stats', label: 'Statistics', icon: BarChart3 },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4 mr-2 inline" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Search and Filters */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                icon={<Search className="h-4 w-4 text-gray-400" />}
              />
            </div>
            {activeTab === 'suppliers' && (
              <select
                value={supplierFilter}
                onChange={(e) => setSupplierFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                <option value="formal">Formal</option>
                <option value="informal">Informal</option>
                <option value="mixed">Mixed</option>
              </select>
            )}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'suppliers' && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSuppliers.map((supplier) => (
                    <tr key={supplier.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{supplier.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSupplierTypeColor(supplier.type)}`}>
                          {supplier.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          {supplier.contactInfo?.phone && <div>{supplier.contactInfo.phone}</div>}
                          {supplier.contactInfo?.email && <div className="text-xs text-gray-500">{supplier.contactInfo.email}</div>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getVerificationStatusIcon(supplier.verificationStatus)}
                          <span className="ml-2 text-sm text-gray-900">{supplier.verificationStatus}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(supplier.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'transactions' && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tag</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTransactions.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(transaction.purchaseDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSupplierTypeColor(transaction.supplierType)}`}>
                          {transaction.supplierType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTransactionTagColor(transaction.transactionTag)}`}>
                          {transaction.transactionTag.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="max-w-xs truncate">
                          {transaction.items.map((item, index) => (
                            <div key={index} className="text-xs">
                              {item.name} x{item.quantity}
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(transaction.totalAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          transaction.paymentMethod === 'cash' ? 'bg-green-100 text-green-800' :
                          transaction.paymentMethod === 'mobile' ? 'bg-blue-100 text-blue-800' :
                          'bg-purple-100 text-purple-800'
                        }`}>
                          {transaction.paymentMethod}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          {transaction.location ? (
                            <>
                              <MapPin className="h-4 w-4 text-gray-400 mr-1" />
                              {transaction.location}
                            </>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'batch' && (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Batch Transactions</h3>
              <p className="text-gray-600">Batch transaction management coming soon...</p>
            </div>
          )}

          {activeTab === 'stats' && stats && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Supplier Breakdown</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Formal Suppliers</span>
                      <span className="font-medium">{stats.supplierBreakdown.formal}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Informal Suppliers</span>
                      <span className="font-medium">{stats.supplierBreakdown.informal}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Mixed Suppliers</span>
                      <span className="font-medium">{stats.supplierBreakdown.mixed}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Transaction Tags</h3>
                  <div className="space-y-3">
                    {Object.entries(stats.transactionTagBreakdown).map(([tag, count]) => (
                      <div key={tag} className="flex justify-between">
                        <span className="text-sm text-gray-600">{tag.replace('_', ' ')}</span>
                        <span className="font-medium">{String(count)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm text-gray-600">Total Transactions</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.totalTransactions}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Amount</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalAmount)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Average Transaction</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.averageTransactionValue)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InformalEconomyPage;
